import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import loteService, { LoteNumeracion } from '../../services/loteService';
import { getApiErrorMessage } from '../../utils/apiError';
import { useSnackbar } from 'notistack';

export interface LoteAsistenteDialogProps {
  open: boolean;
  onClose: () => void;
  codigoArticulo?: string;
  esSerie?: boolean;
  /** Cantidad de números a generar (p. ej. cantidad de filas del diálogo de asignación). */
  cantidadInicial?: number;
  onGenerated: (numeros: string[]) => void;
}

const LoteAsistenteDialog: React.FC<LoteAsistenteDialogProps> = ({
  open,
  onClose,
  codigoArticulo,
  esSerie = false,
  cantidadInicial = 1,
  onGenerated,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [numeraciones, setNumeraciones] = useState<LoteNumeracion[]>([]);
  const [idNumeracion, setIdNumeracion] = useState<number | ''>('');
  const [cantidad, setCantidad] = useState(1);
  const [preview, setPreview] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCantidad(Math.max(1, Math.min(500, Number(cantidadInicial) || 1)));
    loteService
      .listNumeracion()
      .then((list) => {
        setNumeraciones(list);
        const preferred =
          list.find((n) => n.nombre.toLowerCase().includes(esSerie ? 'serie' : 'lote')) ?? list[0];
        setIdNumeracion(preferred?.idLoteNumeracion ?? '');
      })
      .catch(() => setNumeraciones([]));
  }, [open, esSerie, cantidadInicial]);

  useEffect(() => {
    if (!open || !idNumeracion) {
      setPreview([]);
      return;
    }
    let cancelled = false;
    loteService
      .previewNumeracion({
        idLoteNumeracion: Number(idNumeracion),
        codigoArticulo,
        cantidad,
      })
      .then((nums) => {
        if (!cancelled) setPreview(nums);
      })
      .catch(() => {
        if (!cancelled) setPreview([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, idNumeracion, cantidad, codigoArticulo]);

  const handleGenerar = async () => {
    if (!idNumeracion) return;
    setLoading(true);
    try {
      const result = await loteService.generarNumeracion({
        idLoteNumeracion: Number(idNumeracion),
        codigoArticulo,
        esSerie,
        cantidad,
      });
      onGenerated(result.numeros);
      enqueueSnackbar('Números generados', { variant: 'success' });
      onClose();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo generar la numeración'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Asistente de numeración {esSerie ? 'de series' : 'de lotes'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Máscara</InputLabel>
            <Select
              label="Máscara"
              value={idNumeracion}
              onChange={(e) => setIdNumeracion(e.target.value as number)}
            >
              {numeraciones.map((n) => (
                <MenuItem key={n.idLoteNumeracion} value={n.idLoteNumeracion}>
                  {n.nombre} ({n.mascara})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={esSerie ? 'Cantidad de series' : 'Cantidad de números de lote'}
            type="number"
            value={cantidad}
            onChange={(e) => setCantidad(Math.max(1, Number(e.target.value) || 1))}
            inputProps={{ min: 1, max: 500 }}
            helperText={
              esSerie
                ? undefined
                : 'Se genera un número por fila del diálogo de asignación (podés ajustar la cantidad).'
            }
          />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Vista previa
            </Typography>
            {preview.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Sin preview
              </Typography>
            ) : (
              preview.map((n) => (
                <Typography key={n} variant="body2" fontFamily="monospace">
                  {n}
                </Typography>
              ))
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleGenerar} disabled={loading || !idNumeracion}>
          Generar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoteAsistenteDialog;
